import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { DrumPortrait } from "@/components/DrumPortrait";
import { Reveal } from "@/components/Reveal";
import { api, formatCHF } from "@/lib/api";
import { PaymentChoice } from "@/components/PaymentChoice";
import { GiftCodeField, remiseCadeau } from "@/components/GiftCodeField";
import { WyrdGate } from "@/components/WyrdGate";
import { useNoIndex } from "@/lib/seo";
import { useSeo } from "@/lib/seo";

const Checkout = () => {
  useSeo({ title: "Commande · Entre Les Mondes", description: "", noindex: true });
  useNoIndex();
  const { items, total, clear } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState({ nom: "", email: "", adresse: "", npa: "", ville: "", pays: "Suisse" });
  const [paiement, setPaiement] = useState("en_ligne");
  const [carte, setCarte] = useState(null);
  const [loading, setLoading] = useState(false);
  const [membre, setMembre] = useState(null);
  const totalWyrd = membre ? Math.round(total * 90) / 100 : total;
  const remise = remiseCadeau(carte, totalWyrd);
  const totalDu = totalWyrd - remise;

  useEffect(() => {
    document.title = "Paiement — Entre les Mondes";
    if (items.length === 0) navigate("/panier", { replace: true });
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const order = await api.order({
        client: form,
        items: items.map((i) => ({ product_id: i.id, quantite: 1 })),
        paiement,
        origin_url: window.location.origin,
        code_cadeau: carte?.code || null,
      });
      if (order.checkout_url) {
        window.location.assign(order.checkout_url);
        return;
      }
      clear();
      navigate("/confirmation", { state: { order } });
    } catch (err) {
      toast.error(err.response?.data?.detail || "La commande n'a pas abouti, merci de réessayer.");
      setLoading(false);
    }
  };

  if (items.length === 0) return null;

  return (
    <div className="pt-40 pb-24">
      <div className="max-w-6xl mx-auto px-6">
        <Link to="/panier" data-testid="checkout-back" className="inline-flex items-center gap-2 text-sm text-fumee hover:text-bark link-line mb-8">
          <ArrowLeft className="w-4 h-4" /> Retour au panier
        </Link>
        <Reveal>
          <p className="eyebrow mb-4">Dernière étape</p>
          <h1 className="font-serif text-4xl lg:text-5xl text-bark mb-12">Votre commande</h1>
        </Reveal>

        <form onSubmit={submit} className="grid lg:grid-cols-12 gap-12 items-start" data-testid="checkout-form">
          <div className="lg:col-span-7 space-y-10">
            <Reveal>
              <div className="bg-card border border-sand-dark rounded-2xl p-8">
                <h2 className="font-serif text-2xl text-bark mb-6">Où envoyer votre tambour ?</h2>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="co-nom" className="eyebrow block mb-2">Nom complet</label>
                    <input id="co-nom" data-testid="checkout-nom" required value={form.nom} onChange={set("nom")}
                      className="w-full bg-white/60 border border-wood/25 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-terra" />
                  </div>
                  <div>
                    <label htmlFor="co-email" className="eyebrow block mb-2">Courriel</label>
                    <input id="co-email" data-testid="checkout-email" type="email" required readOnly={!!membre} value={form.email} onChange={set("email")}
                      className={`w-full bg-white/60 border border-wood/25 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-terra ${membre ? "opacity-70" : ""}`} />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="co-adresse" className="eyebrow block mb-2">Adresse</label>
                    <input id="co-adresse" data-testid="checkout-adresse" required value={form.adresse} onChange={set("adresse")}
                      className="w-full bg-white/60 border border-wood/25 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-terra" />
                  </div>
                  <div>
                    <label htmlFor="co-npa" className="eyebrow block mb-2">NPA</label>
                    <input id="co-npa" data-testid="checkout-npa" required value={form.npa} onChange={set("npa")}
                      className="w-full bg-white/60 border border-wood/25 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-terra" />
                  </div>
                  <div>
                    <label htmlFor="co-ville" className="eyebrow block mb-2">Ville</label>
                    <input id="co-ville" data-testid="checkout-ville" required value={form.ville} onChange={set("ville")}
                      className="w-full bg-white/60 border border-wood/25 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-terra" />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="co-pays" className="eyebrow block mb-2">Pays</label>
                    <select id="co-pays" data-testid="checkout-pays" value={form.pays} onChange={set("pays")}
                      className="w-full bg-white/60 border border-wood/25 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-terra">
                      {["Suisse", "France", "Belgique", "Luxembourg"].map((p) => <option key={p}>{p}</option>)}
                    </select>
                  </div>
                </div>
                {!membre ? <div className="mt-6"><WyrdGate benefit onVerified={(email, r) => { setMembre(r); setForm((f) => ({ ...f, email })); }} /></div>
                  : <p className="mt-6 rounded-xl border border-sage/40 bg-sage/10 px-4 py-3 text-sm text-bark" data-testid="checkout-wyrd-benefit-ok">Avantage Wyrd confirmé{membre.prenom ? ` — ${membre.prenom}` : ""}. La remise de 10 % est appliquée.</p>}
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="bg-card border border-sand-dark rounded-2xl p-8">
                <h2 className="font-serif text-2xl text-bark mb-2">Paiement</h2>
                <p className="text-xs text-fumee mb-6">Paiement en ligne immédiat par TWINT ou carte (Stripe), ou règlement par facture.</p>
                <PaymentChoice value={paiement} onChange={setPaiement} testidPrefix="checkout" />
                <div className="mt-6 pt-6 border-t border-sand-dark">
                  <GiftCodeField carte={carte} onChange={setCarte} testidPrefix="checkout" />
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15} className="lg:col-span-5">
            <div className="bg-earth grain text-sand rounded-2xl p-8 sticky top-28">
              <h2 className="font-serif text-2xl mb-6 relative z-[2]">Récapitulatif</h2>
              <div className="relative z-[2] space-y-4">
                {items.map((item) => (
                  <div key={item.id} className="flex items-center gap-4">
                    {item.image ? (
                      <img src={item.image} alt={item.nom} className="w-12 h-12 rounded-lg object-cover shrink-0" />
                    ) : (
                      <div className="w-12 h-12 bg-sand/10 rounded-lg p-1.5 shrink-0">
                        <DrumPortrait peau={item.couleur_peau} bois={item.couleur_bois} />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{item.nom}</p>
                      <p className="text-xs text-sand/50">{item.numero}</p>
                    </div>
                    <span className="text-sm">{formatCHF(item.prix)}</span>
                  </div>
                ))}
                <div className="flex justify-between text-sm text-sand/60 pt-4 border-t border-sand/15">
                  <span>Livraison</span><span>Offerte</span>
                </div>
                {remise > 0 && (
                  <div className="flex justify-between text-sm text-sand/80" data-testid="checkout-remise">
                    <span>Carte cadeau {carte.code}</span><span>− {formatCHF(remise)}</span>
                  </div>
                )}
                {membre && <div className="flex justify-between text-sm text-sand/80" data-testid="checkout-wyrd-discount"><span>Avantage Wyrd</span><span>− {formatCHF(total - totalWyrd)}</span></div>}
                <div className="flex justify-between items-baseline">
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-sand/60">{remise > 0 ? "Reste à payer" : "Total"}</span>
                  <span className="font-serif text-3xl" data-testid="checkout-total">{formatCHF(totalDu)}</span>
                </div>
                <button type="submit" data-testid="checkout-submit" disabled={loading}
                  className="w-full mt-4 py-4 rounded-full bg-terra text-earth text-sm tracking-wide hover:bg-sand hover:text-earth transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-3">
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {loading ? "Redirection…" : totalDu === 0 ? "Confirmer la commande" : paiement === "en_ligne" ? "Confirmer et payer (TWINT / carte)" : "Confirmer la commande"}
                </button>
                <p className="text-[11px] text-sand/45 leading-relaxed">
                  En confirmant, vous acceptez nos <Link to="/cgv" className="underline">CGV</Link>. Retour possible sous 14 jours.
                </p>
              </div>
            </div>
          </Reveal>
        </form>
      </div>
    </div>
  );
};

export default Checkout;
